import { endOfDay, isAfter, isBefore, min, startOfDay, subMonths } from "date-fns/esm";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { DateRange } from "ds/components/Datepicker/types";

import { CSVDownloadAspect, TimeFilter } from "../types";

export const getCSVDownloadLink = (aspectType: CSVDownloadAspect, timeFilter: TimeFilter) => {
  const since = timeFilter.startTime.toISOString().split("T")[0];
  const until = timeFilter.endTime.toISOString().split("T")[0];

  // eslint-disable-next-line @cspell/spellchecker
  return `/usageanalytics/csv?aspect=${aspectType}&since=${since}&until=${until}`;
};

// How far back the rollup tables retain data, so also the floor the picker offers.
const MAX_MONTHS_BACK = 6;

export const getCSVDownloadToday = () => utcToZonedTime(new Date(), "UTC");

export const getCSVDownloadMaxDate = () => endOfDay(getCSVDownloadToday());

export const getCSVDownloadMinDate = () =>
  startOfDay(subMonths(getCSVDownloadToday(), MAX_MONTHS_BACK));

// minDate and maxDate only bound the calendar grid: the field's segments can still be
// arrowed past them, and the endpoint rejects a window wider than six months.
export const validateCSVDownloadRange = ({ start, end }: DateRange): string | undefined => {
  if (isBefore(start, getCSVDownloadMinDate())) {
    return `Start date cannot be more than ${MAX_MONTHS_BACK} months ago`;
  }

  if (isAfter(end, getCSVDownloadMaxDate())) {
    return "End date cannot be in the future";
  }

  if (isAfter(start, end)) {
    return "Start date must be before the end date";
  }

  return undefined;
};

// getCSVDownloadLink reads the UTC calendar day while the picker hands back local dates,
// so each bound's wall clock is reinterpreted as UTC to keep the days the user picked.
export const getCSVDownloadTimeFilter = ({ start, end }: DateRange): TimeFilter => ({
  startTime: zonedTimeToUtc(start, "UTC"),
  endTime: zonedTimeToUtc(end, "UTC"),
});

export const getCSVDownloadRange = (start: Date, end: Date): DateRange => ({
  start: startOfDay(utcToZonedTime(start, "UTC")),
  end: min([endOfDay(utcToZonedTime(end, "UTC")), getCSVDownloadMaxDate()]),
});
